import React from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal.js';
import loading_gif from '../Images/loading.gif';
import './MasterPool.css';

class MasterPool extends React.Component {
	constructor(props) {
		super(props);
		// State management:
		if (this.props.location.state) { // Allow stateful linking
			this.state = this.props.location.state;
		}
		else {
			this.state = {
				error: null,
				isLoaded: false,
				statusCodes: [],
				siteList: [],
				pubList: [],
				pubTypesList: [],
				sortCol: 'publishedDate',
				sortDir: 'desc',
				page: 0,
				totalPages: 0,
				pageSize: 10,
				deleteModalVisible: false,
				approvalConfirmationModal: null,
				pubToDelete: false,
				filterString: '',
				filterArr: {
					authorName: '',
					title: '',
					publicationEvent: '',
					publishedDate: '',
					publicationTypeId: '',
					status: '',
					'organization.name': '',
					origin: '',
					share: '',
					url: '',
					artifactUrl: '' ,
					abstractText: '',
					tracking: '',
					attendees: ''
				}
			};
		}

		// Function bindings:
		this.fetchData = this.fetchData.bind(this);
		this.fetchInitialData = this.fetchInitialData.bind(this);
		this.prevPage = this.prevPage.bind(this);
		this.nextPage = this.nextPage.bind(this);
		this.pageQuickJump = this.pageQuickJump.bind(this);
		this.pageSizeChange = this.pageSizeChange.bind(this);
		this.deletePub = this.deletePub.bind(this);
		this.handleDeleteModalShow = this.handleDeleteModalShow.bind(this);
		this.handleDeleteModalHide = this.handleDeleteModalHide.bind(this);
		this.filterChange = this.filterChange.bind(this);
		this.sortChange = this.sortChange.bind(this);
		this.approveItem = this.approveItem.bind(this);
		this.confirmApproveItem = this.confirmApproveItem.bind(this);
	}

	// Get publications for new sorts and filters
	fetchData() {
		fetch(
			'/webpub-services/publicationAPI/getAllPublications?page=' + this.state.page
				+ '&sort=' + this.state.sortCol + ',' + this.state.sortDir
				+ '&size=' + this.state.pageSize + this.state.filterString
		)
		.then(res => res.json())
		.then(
			(result) => {
				if (result.content && result.content.length && result.content[0].title) { // Results look like we expect
					// If there's something we need to do to these, it could happen here.
					// It might be safer to do it in render(), though.
					let content = [];
					
					let foundIds = [];
					content = result.content.map((pub) => {
						if (!foundIds.includes(pub.id)) {
							foundIds.push(pub.id);
							return pub;
						}
						else return null;
					}).filter(res => res !== null);

					this.setState({
						pubList: content,
						totalPages: result.totalPages,
						isLoaded: true,
						error: false
					});
				}
				else if (result.numberOfElements === 0) { // Results empty, but real
					this.setState({
						pubList: result.content,
						totalPages: result.totalPages,
						isLoaded: true,
						error: false
					});
				}
				else { // Something went wrong. Give up.
					this.setState({
						error: {message: 'Publication results malformed. (MP107)'}
					});
				}
			},

			(error) => {
				this.setState({
					isLoaded: false,
					error: error
				});
			}
		);
	}

	// Get first page of publications and all types for initial page load
	fetchInitialData() {
		if (this.props.userData && this.props.activeOrg && this.props.orgList) { // Make sure our props exist
			const urls = [
				'/webpub-services/publicationAPI/getAllPublications?page=' + this.state.page + '&sort=' + this.state.sortCol
					+ ',' + this.state.sortDir + '&size=10',
				'/webpub-services/publicationTypesAPI/getAllPublicationTypes'
			];

			// Asynchronously retrieve all our stuff in one clump
			Promise.all(
				urls.map((url) => {
					return fetch(url).then(res => res.json())
				}
			))
			.then((data) => { // Everything is resolved at this point. Process it.
				// Process publications
				const pubResult = data[0];
				if (pubResult.content && pubResult.content.length && pubResult.content[0].title) { // Results look like we expect
					// If there's something we need to do to these, it could happen here.
					// It might be safer to do it in render(), though.
					this.setState({
						pubList: pubResult.content,
						totalPages: pubResult.totalPages
					});
				}
				else if (pubResult.numberOfElements === 0) { // Results empty, but real
					this.setState({
						pubList: pubResult.content,
						totalPages: pubResult.totalPages,
						isLoaded: true,
						error: false
					});
				}
				else { // Something went wrong. Give up.
					this.setState({
						error: {message: 'Publication results malformed. (MP67)'}
					});
					return;
				}

				// Process types
				const typesResult = data[1];
				if (typesResult.length && typesResult[0].typeName) { // Results look like we expect
					this.setState({
						pubTypesList: typesResult
					});
				}
				else { // Something went wrong. Give up.
					this.setState({
						error: {message: 'PublicationTypes results malformed. (MP67)'}
					});
					return;
				}

				// If we have reached this point, all our calls were successful. Congratulations!
				this.setState({
					isLoaded: true,
					error: null
				});
			});
		}
	}

	// Get the previous page's results, preserving the current sort
	prevPage() {
		if (this.state.page >= 1) {
			this.setState((prevState, props) => ({
				page: prevState.page - 1
			}), () => {
				this.fetchData();
			});
		}
	}

	// Get the next page's results, preserving the current sort
	nextPage() {
		if (this.state.page < (this.state.totalPages - 1)) {
			this.setState((prevState, props) => ({
				page: prevState.page + 1
			}), () => {
				this.fetchData();
			});
		}
	}
	
	// Jump to page given by user input
	pageQuickJump(changeEvent) {
		const pageNum = parseInt(changeEvent.currentTarget.value);
		
		if (
			!isNaN(pageNum) // Control for users being users
			&& this.state.page !== pageNum
		) {
			this.setState({
				page: pageNum - 1
			}, () => {
				this.fetchData();
			});
		}
	}

	// Change number of results displayed per page
	pageSizeChange(changeEvent) {
		this.setState({
			pageSize: changeEvent.currentTarget.value,
			page: 0
		}, () => {
			this.fetchData();
		});
	}
	
	// Creates a new delete modal for the selected publication
	handleDeleteModalShow(pubToDelete) {
		if (pubToDelete && pubToDelete.id) { // Sanity check
			this.setState({
				deleteModalVisible: true,
				pubToDelete: pubToDelete
			});
		}
		else {
			console.log('Delete modal called without acceptable ID (MP183)');
		}
	}

	// Destroys the existing delete modal
	handleDeleteModalHide() {
		this.setState({
			deleteModalVisible: false,
			pubToDelete: false
		}, () => {
			this.fetchData();
		});
	}

	// Delete the selected publication (DANGER! DANGER, WILL ROBINSON)
	deletePub() {
		const pubToDelete = this.state.pubToDelete;
		if (!(pubToDelete && pubToDelete.id)) { // Extra sanity check.
			this.handleDeleteModalHide();
		}
		if (this.props.userData.id === pubToDelete.createdUser) { // Check again. Be sure.
			fetch(
				'/webpub-services/publicationAPI/deletePublicationById/' + pubToDelete.id,
				{
					method: 'DELETE'
				}
			)
			.then(
				(result) => {
					// TODO: deal with return values, I guess?
					this.handleDeleteModalHide();
				},

				(error) => {
					// TODO: deal with return values, I guess?
				}
			);
		}
	}

	// Alters the filter string due to user input, then fetches data with the new string
	filterChange(event) {
		const target = event.target;
		var currentFilter = this.state.filterArr;
		var newString = '';

		// Do some sanitizing
		var filterVal = target.value.replace(/[^a-z0-9 .,_-]/gim,"");

		if (target.name === 'publishedDate' && filterVal !== '') {
			filterVal = parseInt(filterVal); // Strip out any non-integer characters
			if (!isNaN(filterVal) && filterVal > 0 && filterVal < 9999) {
				filterVal = '' + filterVal; // Get back to a string for the next step
				filterVal.padStart(4, '0');
			}
			else {
				filterVal = currentFilter['publishedDate'];
			}
		}

		currentFilter[target.name] = filterVal;

		// Build URL filter string
		Object.keys(currentFilter).map(function(objectKey, index) {
			if (currentFilter[objectKey] !== null && currentFilter[objectKey] !== '') {
				newString += '&' + objectKey + '=' + currentFilter[objectKey];
			}

			return true;
		});

		this.setState({
			filterArr: currentFilter,
			filterString: newString,
			page: 0
		}, () => {
			this.fetchData();
		});
	}

	// Alters the sort variables due to user input, then fetches data with the new sorting parameters
	sortChange(event) {
		const target = event.currentTarget;
		const targetName = target.attributes.name.value;

		if (targetName === this.state.sortCol) {
			const newDir = this.state.sortDir === 'asc' ? 'desc' : 'asc';

			this.setState({ sortDir: newDir }, () => { this.fetchData(); });
		}
		else if (targetName === 'abstractText' || targetName === 'artifactUrl') { // These two sort backwards
			this.setState({
				sortDir: 'desc',
				sortCol: targetName
			}, () => { this.fetchData(); });
		}
		else {
			this.setState({
				sortDir: 'asc',
				sortCol: targetName
			}, () => { this.fetchData(); });
		}
	}

	// Change approval status of an item
	approveItem(item) {
		const approvalModal = (
			<Modal>
				<div className='modal' aria-modal='true'>
					<div role='dialog' className='master-pool-modal'>
						<header>
							<span>Confirm Approval</span>
							<button
								onClick={() => this.setState({approvalConfirmationModal: null})}
								type='button'
								aria-label='close'
							>
								X
							</button>
						</header>
							<div className='master-pool-modal-content' style={{textAlign: 'center'}} >
								Are you sure you want to approve this item?
							</div>
						<footer>
							<button
								onClick={
									() => {this.confirmApproveItem(item)}
								}
								type='button'
								aria-label='confirm'
							>
								Confirm
							</button>
							<button
								onClick={() => this.setState({approvalConfirmationModal: null})}
								type='button'
								aria-label='cancel'
							>
								Cancel
							</button>
						</footer>
					</div>
				</div>
			</Modal>
		);

		this.setState({
			approvalConfirmationModal: approvalModal
		});
	}

	confirmApproveItem(item) {
		let newItem = item;
		newItem.approved = !newItem.approved;
		newItem.modifiedUser = this.props.userData.id;
		newItem.modifiedDate = Date.now();

		fetch(
			'webpub-services/publicationAPI/updatePublication/', {
				method: 'PUT',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(newItem)
			}
		)
		.then(res => res.json())
		.then(
			(result) => { // Request received.
				// I don't really know what we can expect here except success.
				this.setState({approvalConfirmationModal: null},
					this.fetchData());
			},

			(error) => { // something went wrong!
				this.setState({
					error: error,
					isLoaded: false
				});
			}
		);
	}

	// Initial page load
	componentDidMount() {
		this.fetchInitialData();
	}

	render() {
		// Sanity checking
		if (this.state.error) {
			return <div className='error-text'>
				Error: {
					this.state.error.message
					? this.state.error.message
					: 'An unknown error has occurred (MP58)'
				}
			</div>;
		}
		else if (!this.state.isLoaded) { // Display nothing until we're ready for user input
			return <div>Loading <img src={loading_gif} alt='loading gif' style={{ verticalAlign: 'middle' }} /></div>;
		}

		const statusCodes = ['Planned', 'Submitted', 'In Review', 'In Press', 'Published'];

		// User can approve/delete items from these orgs:
		const adminOrgs = this.props.userData.permissions.map((org) => {
			if (org.accessLevel >= 2) { // 0 is no access, 1 is read/submit, 2 is admin, 3 is a super-admin
				return org.organizationId;
			}
			else {
				return null;
			}
		}).filter(item => item !== null);

		// Build table contents
		const content = this.state.pubList.map((item) => {
			const pubType = this.state.pubTypesList.filter(pubType => pubType.id === item.publicationTypeId)[0];
			const pubDate = new Date(item.publishedDate.replace(' ', 'T'));

			let itemDate = (pubDate.getMonth()+1) + '/' + pubDate.getDate() + '/' + pubDate.getFullYear();

			const crDate = new Date(item.createdDate.replace(' ', 'T'));
			let itemCreateDate = (crDate.getMonth()+1) + '/' + crDate.getDate() + '/' + crDate.getFullYear();

			const moDate = new Date(item.modifiedDate.replace(' ', 'T'));
			let itemModifiedDate = (moDate.getMonth()+1) + '/' + moDate.getDate() + '/' + moDate.getFullYear();

			// Enable/disable trash can depending on user
			const deleteIcon = (this.props.userData.id === item.createdUser || adminOrgs.includes(item.organizationId))
			? (
				<span
					className='icon-trash ui-icon tooltip clickable'
					onClick={() => this.handleDeleteModalShow(item)}
				>
					<span className='tooltiptext-bottom'>Delete</span>
				</span>
			)
			: null;

			// author aggregation
			const orderedAuthors = item.authors.map((author) => {
				const lastName = author.lastName;
				const firstInit = (author.firstName !== null && author.firstName.length)
					? ' ' + author.firstName.charAt(0) + '.'
					: '';
				const middleInit = (author.middleName !== null && author.middleName.length)
					? author.middleName.charAt(0) + '.'
					: '';
				const citationName = lastName + (firstInit !== '' ? firstInit + middleInit : '');

				return citationName;
			}).sort().join(', ');

			const publishedTo = item.sites.map((site) => {
				const publishedToConst = site.site.url
				? site.site.url.length > 25
					? <div className='tooltip' key={'item-' + item.id + '-site-' + site.site.id} >
						{site.site.url.substring(0, 25) + '...'}
						<span className='tooltiptext-bottom'>{site.site.url}</span>
					</div>
					: <div key={'item-' + item.id + '-site-' + site.site.id} >{site.site.url}</div>
				: site.site.name
					? site.site.name // Fall back to name
					: 'Page #' + site.site.id // Last resort is the id
				;

				return publishedToConst;
			});

			let approvalIcon = (
				<span
					className='icon-filled-checkmark ui-icon tooltip'
				>
					<span className='tooltiptext-bottom'>Approved!</span>
				</span>
			);
			if (!item.approved) {
				if (adminOrgs.includes(item.organizationId)) { // User is an admin and thus can approve
					approvalIcon = (
						<span
							className='icon-checkmark ui-icon tooltip clickable'
							onClick={() => this.approveItem(item)}
						>
							<span className='tooltiptext-bottom'>Approve</span>
						</span>
					);
				}
				else { // User only has read access and thus can only see if this is approved or not
					approvalIcon = (
						<span
							className='icon-checkmark ui-icon tooltip'
						>
							<span className='tooltiptext-bottom'>Awaiting Approval</span>
						</span>
					);
				}
			}

			const editIcon = (
				<Link
					to={{
						pathname: '/edit',
						state: {
							pubData: item
						}
					}}
					className='icon-pencil ui-icon tooltip'
				>
					<span className='tooltiptext-bottom'>Edit</span>
				</Link>
			);

			return (
				<tr key={'pubrow-' + item.id} onClick={(e) => {e.currentTarget.classList.toggle('master-pool-selected-row')}}>
					<td style={{ textAlign: 'left' }} >{orderedAuthors}</td>
					<td style={{ textAlign: 'left' }} >{item.title}</td>
					<td style={{ textAlign: 'left' }} >{item.publicationEvent}</td>
					<td>{itemDate}</td>
					<td>{pubType.typeName}</td>
					<td>{statusCodes[item.status]}</td>
					<td style={{ textAlign: 'left' }} >{
						item.organization.name.length > 13
							? <div className='tooltip' >
								{item.organization.name.substring(0, 13) + '...'}
								<span className='tooltiptext-bottom'>{item.organization.name}</span>
							</div>
							: item.organization.name
					}</td>
					<td>{item.origin}</td>
					<td>{item.share ? 'Yes' : 'No'}</td>
					<td style={{ textAlign: 'left' }} >{publishedTo}</td>
					<td>
						{
							item.artifactUrl
							?
								<a
									href={item.artifactUrl}
									target='_blank'
									rel='noopener noreferrer'
									className='tooltip'
									style={{textDecoration: 'none', color: 'inherit'}}
								>
									Yes
									<span
										className='tooltiptext-bottom'
									>
										{item.artifactUrl}
									</span>
								</a>
							: 'No'
						}
					</td>
					<td>
						{
							item.abstractText
							?
								<span className='tooltip clickable'>Yes
									<span className='tooltiptext-bottom'>{item.abstractText}</span>
								</span>
							: 'No'
						}
					</td>
					<td>
						{
							item.tracking ? 'Yes' : 'No'
						}
					</td>
					<td>
						{
							item.attendees
						}
					</td>
					<td>
						{
							itemCreateDate
						}
					</td>
					<td>
						{
							item.createdUserInfo.name
						}
					</td>
					<td>
						{
							itemModifiedDate
						}
					</td>
					<td>
						{
							item.modifiedUserInfo.name
						}
					</td>
					<td style={{ textAlign: 'left' }} >
						{approvalIcon}

						{editIcon}

						{deleteIcon}
					</td>
				</tr>
			);
		});

		// Make sure the user knows exactly how dangerous deleting publications is
		const deleteModal = (this.state.deleteModalVisible)
			? (
				<Modal>
					<div className='modal' aria-modal='true'>
						<div role='dialog' className='master-pool-modal'>
							<header>
								<span>Confirm</span>
								<button
									onClick={this.handleDeleteModalHide}
									type='button'
									aria-label='close'
								>
									X
								</button>
							</header>
								<div className='master-pool-modal-content' style={{textAlign: 'center'}} >
									Are you sure? <br/>
									Deleting this publication will remove it from the pool and from ALL pages! <br/>
									This action CANNOT be undone. The data CANNOT be retrieved.
								</div>
							<footer>
								<button
									onClick={this.deletePub}
									type='button'
									aria-label='confirm'
								>
									Confirm
								</button>
								<button
									onClick={this.handleDeleteModalHide}
									type='button'
									aria-label='cancel'
								>
									Cancel
								</button>
							</footer>
						</div>
					</div>
				</Modal>
			)
			: null;

		const paginationContent = (
		<tr>
			<td
				onClick={this.prevPage}
				className={'clickable pagination-navigation-' + ((this.state.page >= 1) ? 'active' : 'inactive')}
			>
				Previous
			</td>
			<td colSpan={17} >
				Page { }
				<input 
					type='text'
					value={this.state.page + 1}
					style={{ maxWidth: '1vw', textAlign: 'center' }}
					onChange={this.pageQuickJump}
				/>
				{ } of { this.state.totalPages }
			</td>
			<td
				onClick={this.nextPage}
				className={'clickable pagination-navigation-' + ((this.state.page < (this.state.totalPages - 1)) ? 'active' : 'inactive')}
			>
				Next
			</td>
		</tr>
		);

		// Put it all together:
		return (
			<div>
				<h2>Publication Pool: Approve, Edit, and Delete</h2>
				<div style={{overflowX:'auto'}}>
				<table className='master-pool-table'>
					<thead>
						<tr className='master-pool-head-row'>
							<th className='master-pool-header clickable' key='header-author' name='firstAuthor' onClick={this.sortChange} >
								Author { }
								{this.state.sortCol === 'firstAuthor' 
									? this.state.sortDir === 'asc' 
										? <button 
											className='icon-sort-up-arrow ui-icon' 
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}} 
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-title' name='title' onClick={this.sortChange} >
								Title { }
								{this.state.sortCol === 'title'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-publicationEvent' name='publicationEvent' onClick={this.sortChange} >
								Publication/Event { }
								{this.state.sortCol === 'publicationEvent'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-date' name='publishedDate' onClick={this.sortChange} >
								Date { }
								{this.state.sortCol === 'publishedDate'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-type' name='publicationType.typeName' onClick={this.sortChange} >
								Type { }
								{this.state.sortCol === 'publicationType.typeName'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-status' name='status' onClick={this.sortChange} >
								Status { }
								{this.state.sortCol === 'status'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-org' name='organization.name' onClick={this.sortChange} >
								Organization { }
								{this.state.sortCol === 'organization.name'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-origin' name='origin' onClick={this.sortChange} >
								Origin { }
								{this.state.sortCol === 'origin'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-share' name='share' onClick={this.sortChange} >
								Share { }
								{this.state.sortCol === 'share'
									? this.state.sortDir === 'asc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header' key='header-publishedTo' >
								Where Posted
							</th>
							<th className='master-pool-header clickable' key='header-artifactUrl' name='artifactUrl' onClick={this.sortChange} >
								Artifact { }
								{this.state.sortCol === 'artifactUrl'
									? this.state.sortDir === 'desc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-abstractText' name='abstractText' onClick={this.sortChange} >
								Abstract { }
								{this.state.sortCol === 'abstractText'
									? this.state.sortDir === 'desc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header' key='header-tracking' >
								Tracking
							</th>
							<th className='master-pool-header' key='header-attendees' >
								Attendees
							</th>
							<th className='master-pool-header clickable' key='header-created-date' name='createdDate' onClick={this.sortChange} >
								Created { }
								{this.state.sortCol === 'createdDate'
									? this.state.sortDir === 'desc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-created-user' name='createdUserInfo' onClick={this.sortChange} >
								Creator { }
								{this.state.sortCol === 'createdUserInfo'
									? this.state.sortDir === 'desc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-modified-date' name='modifiedDate' onClick={this.sortChange} >
								Modified { }
								{this.state.sortCol === 'modifiedDate'
									? this.state.sortDir === 'desc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header clickable' key='header-modified-user' name='modifiedUserInfo' onClick={this.sortChange} >
								Modifier { }
								{this.state.sortCol === 'modifiedUserInfo'
									? this.state.sortDir === 'desc'
										? <button
											className='icon-sort-up-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
										: <button
											className='icon-sort-down-arrow ui-icon'
											style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
										/>
									: <button
										className='icon-sort-up-and-down-arrow ui-icon'
										style={{display: 'inline-block', border: 'none', backgroundColor: 'inherit', verticalAlign: 'inherit'}}
									/>
								}
							</th>
							<th className='master-pool-header' key='header-actions' >
								Actions
							</th>
						</tr>
						<tr className='master-pool-head-row'>
							<th className='master-pool-header'>{ /* Author */ }
							<input
									type='text'
									name='authorName'
									maxLength={15}
									onChange={this.filterChange}
									value={this.state.filterArr['authorName']}
									className='master-pool-input-15-ex'
								/>
							</th>
							<th className='master-pool-header'>{ /* Title */ }
								<input
									type='text'
									name='title'
									maxLength={15}
									onChange={this.filterChange}
									value={this.state.filterArr['title']}
									className='master-pool-input-15-ex'
								/>
							</th>
							<th className='master-pool-header'>{ /* Publication/Event */ }
								<input
									type='text'
									name='publicationEvent'
									maxLength={15}
									onChange={this.filterChange}
									value={this.state.filterArr['publicationEvent']}
									className='master-pool-input-15-ex'
								/>
							</th>
							<th className='master-pool-header'>{ /* Date */ }
								<input
									type='number'
									name='publishedDate'
									min={0}
									max={3000}
									onChange={this.filterChange}
									value={this.state.filterArr['publishedDate']}
									className='master-pool-input-date'
								/>
							</th>
							<th className='master-pool-header'
									>{ /* Publication Type */ }
								<select
									name='publicationTypeId'
									onChange={this.filterChange}
									value={this.state.filterArr['publicationTypeId']}
									className='master-pool-select-type'
								>
									<option value='' >All</option>
									{
										this.state.pubTypesList.map((code) => {
											return <option value={code.id} key={'type-' + code.id} >{code.typeName}</option>;
										})
									}
								</select>
							</th>
							<th className='master-pool-header'>{ /* Status */ }
								<select
									name='status'
									onChange={this.filterChange}
									value={this.state.filterArr['status']}
									className='master-pool-select-status'
								>
									<option value='' >All</option>
									{
										statusCodes.map((name, idx) => {
											return <option value={idx} key={'status-' + idx} >{name}</option>;
										})
									}
								</select>
							</th>
							<th className='master-pool-header'>{ /* Org */ }
								<select
									name='organizationId'
									onChange={this.filterChange}
									value={this.state.filterArr['organizationId']}
									className='master-pool-select-org'
								>
									<option value='' >All</option>
									{
										this.props.orgList !== undefined && 
										this.props.orgList.sort((a, b) => {return a.name.localeCompare(b.name);}).map((org) => {
											return <option value={org.id} key={'orgid-' + org.id} >{ org.name }</option>;
										})
									}
								</select>
							</th>
							<th className='master-pool-header'>{ /* Origin */ }
								<select
									name='origin'
									onChange={this.filterChange}
									value={this.state.filterArr['origin']}
									className='master-pool-select-origin'
								>
									<option value='' >All</option>
									<option value="Internal">Internal</option>
									<option value="External">External</option>
								</select>
							</th>
							<th className='master-pool-header'>{ /* Share */ }
								<select
									name='share'
									onChange={this.filterChange}
									value={this.state.filterArr['share']}
									className='master-pool-yes-no'
								>
									<option value='' >All</option>
									<option value="true">Yes</option>
									<option value="false">No</option>
								</select>
							</th>
							<th className='master-pool-header'>{ /* Published To */ }
								<input
									type='text'
									name='url'
									maxLength={15}
									onChange={this.filterChange}
									value={this.state.filterArr['url']}
									className='master-pool-input-15-ex'
								/>
							</th>
							<th className='master-pool-header'>{ /* Artifact */ }
								<select
									name='artifactUrl'
									onChange={this.filterChange}
									value={this.state.filterArr['artifactUrl']}
									className='master-pool-yes-no'
								>
									<option value='' >All</option>
									<option value="yes">Yes</option>
									<option value="no">No</option>
								</select>
							</th>
							<th className='master-pool-header'>{ /* Abstract */ }
								<select
									name='abstractText'
									onChange={this.filterChange}
									value={this.state.filterArr['abstractText']}
									className='master-pool-yes-no'
								>
									<option value='' >All</option>
									<option value="yes">Yes</option>
									<option value="no">No</option>
								</select>
							</th>
							<th className='master-pool-header'>{ /* Tracking */ }
								<select
									name='tracking'
									onChange={this.filterChange}
									value={this.state.filterArr['tracking']}
									className='master-pool-yes-no'
								>
									<option value='' >All</option>
									<option value="true">Yes</option>
									<option value="false">No</option>
								</select>
							</th>
							<th className='master-pool-header'>{ /* Attendees */ }
								<select
									name='attendees'
									onChange={this.filterChange}
									value={this.state.filterArr['attendees']}
									className='master-pool-yes-no'
								>
									<option value='' >All</option>
									<option value='-1'>{'<25'}</option>
									<option value='-2'>{'<50'}</option>
									<option value='-3'>{'<75'}</option>
									<option value='-4'>{'<100'}</option>
									<option value='-5'>{'<200'}</option>
									<option value='-6'>{'<300'}</option>
									<option value='-7'>{'<400'}</option>
									<option value='-8'>{'<500'}</option>
									<option value='-9'>{'>500'}</option>
								</select>
							</th>
							<th className='master-pool-header'>{ /* Created [Date] */ }
								<input
									type='number'
									name='createdDate'
									min={0}
									max={3000}
									onChange={this.filterChange}
									value={this.state.filterArr['createdDate']}
									className='master-pool-input-date'
								/>
							</th>
							<th className='master-pool-header'>{ /* Creator */ }
								<input
									type='text'
									name='createdUser'
									maxLength={15}
									onChange={this.filterChange}
									value={this.state.filterArr['createdUser']}
									className='master-pool-input-15'
								/>
							</th>
							<th className='master-pool-header'>{ /* Modified [Date] */ }
								<input
									type='number'
									name='modifiedDate'
									min={0}
									max={3000}
									onChange={this.filterChange}
									value={this.state.filterArr['modifiedDate']}
									className='master-pool-input-date'
								/>
							</th>
							<th className='master-pool-header'>{ /* Modifier */ }
								<input
									type='text'
									name='modifiedUser'
									maxLength={15}
									onChange={this.filterChange}
									value={this.state.filterArr['modifiedUser']}
									className='master-pool-input-15'
								/>
							</th>
							<th className='master-pool-header'>{ /* Actions */ }
								<div style={{ width: '50px' }} />
							</th>
						</tr>
					</thead>
					<tbody>
						{content}
					</tbody>
					<tfoot>
						{paginationContent}
					</tfoot>
				</table>
				<div style={{textAlign: 'right'}}>
					<label 
						htmlFor='updatePageSize' 
					>
						Results Per Page: { }
					</label>
					<select
						name='pageSize'
						onChange={this.pageSizeChange}
						value={this.state.pageSize}
					>
						<option value={5}>5</option>
						<option value={10}>10</option>
						<option value={15}>15</option>
						<option value={20}>20</option>
						<option value={25}>25</option>
					</select>
				</div>
				</div>
				{deleteModal}
				{this.state.approvalConfirmationModal}
			</div>
		);
	}
}

export default MasterPool;
